import type { Recipe } from './mealPrep';

/**
 * Nutrition tracker — pure helpers (sibling of `hydration.ts`).
 *
 * Nutrition earns XP by LOGGING CONSISTENCY, not by hitting calorie targets
 * (calories are bidirectional — both over and under are "off", so target-based
 * XP would be judgmental). Instead a day earns XP for tracking: logging up to
 * `MEAL_GOAL` meals fills the day's cap. That's farm-proof (capped per day) and
 * direction-neutral.
 *
 * `sumNutritionXP` is the SINGLE definition of "Nutrition's economy XP", and it
 * ADDS INTO the same `nutrition` area as hydration (`useDisciplines`) while
 * crediting the Personal domain once (`useUserStats`).
 */

import { POINTS, TRACKER_PARAMS } from './levelCalculation';

/**
 * The nutrients Nutrition tracks. ⭐⭐ A value is a number, or `null` = UNKNOWN —
 * never 0 (owner, 2026-10-09: "missing values remain unknown, not zero"). A total
 * that includes an unknown says it is incomplete (`dayTotals`).
 */
export const NUTRIENT_KEYS = ['calories', 'protein', 'carbs', 'fat', 'fibre'] as const;
export type NutrientKey = (typeof NUTRIENT_KEYS)[number];
/** The four that carry targets (fibre is recorded, not targeted, in phase 1). */
export const TARGET_KEYS = ['calories', 'protein', 'carbs', 'fat'] as const;
export type TargetKey = (typeof TARGET_KEYS)[number];
export type NutrientValue = number | null;
export type Nutrients = Record<NutrientKey, NutrientValue>;

export const NUTRIENT_META: Record<NutrientKey, { label: string; short: string; unit: string }> = {
  calories: { label: 'Calories', short: 'kcal', unit: 'kcal' },
  protein: { label: 'Protein', short: 'P', unit: 'g' },
  carbs: { label: 'Carbs', short: 'C', unit: 'g' },
  fat: { label: 'Fat', short: 'F', unit: 'g' },
  fibre: { label: 'Fibre', short: 'Fb', unit: 'g' },
};

export const UNKNOWN_NUTRIENTS: Nutrients = { calories: null, protein: null, carbs: null, fat: null, fibre: null };

/** Where a meal's numbers came from — shown on the meal so totals are honest about their basis. */
export type MealSource = 'user' | 'label' | 'recipe' | 'legacy_preset';
export const MEAL_SOURCE_LABEL: Record<MealSource, string> = {
  user: 'typed by you',
  label: 'from a label',
  recipe: 'from a recipe',
  legacy_preset: 'from your old quick-add',
};

/**
 * ⭐ What a meal — or a plan occasion — is FOR (owner, 2026-10-17): a meal carries one or
 * more as tags (eggs can be breakfast AND lunch); a plan occasion has one as its type,
 * and planning it offers the meals with that tag first. Stored lower-case (mig 0298).
 */
export const OCCASION_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'] as const;
export type OccasionType = (typeof OCCASION_TYPES)[number];
export const OCCASION_TYPE_LABEL: Record<OccasionType, string> = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack' };

/** Known tags only, each once, in the day's order — anything else is dropped, never guessed. */
export function readOccasionTags(x: unknown): OccasionType[] {
  const got = new Set(Array.isArray(x) ? x : []);
  return OCCASION_TYPES.filter(t => got.has(t));
}

/** An occasion's type from what's stored — null (any meal) unless it's a known one. */
export function readOccasionType(x: unknown): OccasionType | null {
  return (OCCASION_TYPES as readonly unknown[]).includes(x) ? (x as OccasionType) : null;
}

/** The type a NAME spells exactly ("Snack", " lunch "), or null — used only to pre-fill a choice the person sees. */
export function occasionTypeOfName(name: string): OccasionType | null {
  return readOccasionType(name.trim().toLowerCase());
}

/** A personal meal (`nutrition_meals`). Nutrients are PER SERVING. */
export interface Meal {
  id: string;
  revision: number;
  name: string;
  servingLabel: string;
  servingGrams: number | null;
  nutrients: Nutrients;
  source: MealSource;
  icon: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Set on meals moved from a saved quick-add preset (makes the move idempotent). */
  legacyPresetId?: string | null;
  /** Optional recipe for preparing (phase 4) — the meal works everywhere without it. */
  recipe?: Recipe | null;
  /** What it's for (mig 0298). Empty = not tagged yet — the Meals list asks for one. */
  occasionTags?: OccasionType[];
  /** A private photo: the object path in `meal-photos` (mig 0301). Null/absent = none. */
  photoPath?: string | null;
}

/** What a logged entry remembers of its meal, frozen at logging time. */
export interface MealSnapshot {
  name: string;
  servingLabel: string;
  servingGrams: number | null;
  /** Per serving, as the meal had them when it was logged. */
  nutrients: Nutrients;
  source: MealSource;
}

/**
 * One thing eaten. The nutrient fields are the amounts AS EATEN (portion applied);
 * `null`/absent = unknown. Entries logged before 2026-10-09 carry the four macros as
 * numbers and no fibre — still valid, read as they are.
 * `mealId` + `mealRevision` + `portion` + `snapshot` are set when it came from a meal:
 * editing or archiving that meal later never changes this entry.
 */
export interface FoodEntry {
  id: string;
  name: string;
  calories?: NutrientValue;
  protein?: NutrientValue;
  carbs?: NutrientValue;
  fat?: NutrientValue;
  fibre?: NutrientValue;
  at: string; // ISO timestamp
  mealId?: string;
  mealRevision?: number;
  /** Servings eaten (1 = one serving). Absent on one-off foods. */
  portion?: number;
  snapshot?: MealSnapshot;
  /** The meal-plan occasion this entry fulfils (phase 3a) — how Today shows a planned meal as eaten. */
  occasionId?: string;
  /** The linked eat-and-tick operation (phase 3b-2), shared with the habit completion it ticked. */
  opId?: string;
}

export interface NutritionDay {
  date: string; // local YYYY-MM-DD
  entries: FoodEntry[];
}

/** A day's total of one nutrient: the known sum, and whether any entry didn't know it. */
export interface NutrientTotal {
  value: number;
  /** At least one entry doesn't know this nutrient — the sum is a floor, not the whole. */
  incomplete: boolean;
  /** At least one entry knows it (false + entries = "unknown", shown as —). */
  known: boolean;
}
export type NutrientTotals = Record<NutrientKey, NutrientTotal>;

/**
 * Daily targets. `null` = no target for that nutrient (not tracked against a number).
 * ⚠️ No invented defaults (owner Q3, 2026-10-09): an account that never saved targets
 * has all four null and the UI says "No targets set".
 */
export type NutritionTargets = Record<TargetKey, number | null>;
export const NO_TARGETS: NutritionTargets = { calories: null, protein: null, carbs: null, fat: null };

/** True when at least one target was deliberately saved. */
export function hasTargets(t: NutritionTargets): boolean {
  return TARGET_KEYS.some(k => typeof t[k] === 'number');
}

/** Meals logged for a full day's XP (the daily cap). */
export const MEAL_GOAL = 3;

/** Economy XP for a fully-tracked nutrition day (tunable; matches hydration). */
export const DAILY_NUTRITION_XP = 50;

/** Icon keys offered by the preset icon picker (each maps to a lucide icon in the UI). */
export const FOOD_ICON_KEYS = [
  'egg', 'eggFried', 'drumstick', 'beef', 'fish', 'salad', 'sandwich', 'soup', 'pizza',
  'milk', 'coffee', 'water', 'cookie', 'donut', 'croissant', 'cake', 'apple', 'banana',
  'carrot', 'cherry', 'grape', 'wheat', 'bean', 'nut', 'popcorn', 'candy', 'utensils',
] as const;

export const DEFAULT_PRESET_ICON = 'utensils';

/** Economy XP for ONE day — proportional to meals logged, capped at MEAL_GOAL. */
export function dayNutritionXP(entryCount: number): number {
  const count = Math.max(0, entryCount || 0);
  // Config-backed (console → XP economy); the consts above are the defaults.
  const ratio = Math.min(1, count / (TRACKER_PARAMS.nutritionMealGoal || MEAL_GOAL));
  return Math.round(ratio * (POINTS.TRACKER_NUTRITION_DAILY ?? DAILY_NUTRITION_XP));
}

/** Total Nutrition economy XP across days (the single source of truth). */
export function sumNutritionXP(days: ReadonlyArray<NutritionDay>): number {
  return days.reduce((sum, d) => sum + dayNutritionXP(d.entries?.length ?? 0), 0);
}

/** An entry's amount of one nutrient — a number, or null when unknown. */
export function entryNutrient(e: FoodEntry, key: NutrientKey): NutrientValue {
  const v = e[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Sum a day's entries per nutrient. An entry that doesn't know a nutrient makes that
 * total `incomplete` — it is never counted as 0 behind the user's back.
 */
export function dayTotals(entries: ReadonlyArray<FoodEntry>): NutrientTotals {
  const out = {} as NutrientTotals;
  for (const key of NUTRIENT_KEYS) {
    let value = 0, known = false, incomplete = false;
    for (const e of entries) {
      const v = entryNutrient(e, key);
      if (v === null) incomplete = true;
      else { value += v; known = true; }
    }
    out[key] = { value: roundNutrient(key, value), known, incomplete };
  }
  return out;
}

/** Calories are whole numbers; grams keep one decimal (¾ of 14 g fat = 10.5 g). */
export function roundNutrient(key: NutrientKey, v: number): number {
  return key === 'calories' ? Math.round(v) : Math.round(v * 10) / 10;
}

/** A clean nutrient value from user input: '' / blank / NaN / negative → null (unknown). */
export function parseNutrient(key: NutrientKey, raw: string | number | null | undefined): NutrientValue {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string' && raw.trim() === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return roundNutrient(key, n);
}

/** Per-serving nutrients × portion; unknown stays unknown. */
export function scaleNutrients(n: Nutrients, portion: number): Nutrients {
  const out = {} as Nutrients;
  for (const key of NUTRIENT_KEYS) {
    const v = n[key];
    out[key] = typeof v === 'number' ? roundNutrient(key, v * portion) : null;
  }
  return out;
}

/** Read a jsonb nutrients object defensively: anything not a finite, non-negative number is unknown. */
export function readNutrients(raw: unknown): Nutrients {
  const obj = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw as Record<string, unknown> : {};
  const out = {} as Nutrients;
  for (const key of NUTRIENT_KEYS) {
    const v = obj[key];
    out[key] = typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;
  }
  return out;
}

/** The portions offered by a stepper; any positive number may be typed. */
export const PORTION_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3] as const;

/** "¾", "1", "1½", "2.3" — a portion as people read it. */
export function formatPortion(p: number): string {
  const whole = Math.floor(p + 1e-9);
  const frac = Math.round((p - whole) * 100) / 100;
  const glyph: Record<number, string> = { 0.25: '¼', 0.5: '½', 0.75: '¾' };
  if (frac === 0) return String(whole);
  if (glyph[frac]) return whole === 0 ? glyph[frac] : `${whole}${glyph[frac]}`;
  return String(Math.round(p * 100) / 100);
}

/** "¾ serving", "1 serving", "1½ servings" — a fraction of one reads singular. */
export const servingWord = (p: number) => (p <= 1 ? 'serving' : 'servings');

/**
 * May food (with no linked habit) be logged on `date`? Never in the future; a past day
 * only when "Allow logging past days" is on, and within the loaded window (the store
 * holds `windowDays` of history — owner Q2, 2026-10-09; linked habits follow their own
 * allowBackfill from phase 3).
 */
export function canLogOn(date: string, today: string, allowPastDays: boolean, oldest: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  if (date > today) return false;
  if (date === today) return true;
  return allowPastDays && date >= oldest;
}

/** 0..100 progress of a macro total toward its target. */
export function macroProgress(total: number, target: number): number {
  const t = Math.max(1, target || 1);
  return Math.min(100, Math.max(0, ((total || 0) / t) * 100));
}

/** A day counts toward the streak once at least one food is logged. */
export function isDayTracked(day: NutritionDay): boolean {
  return (day.entries?.length ?? 0) > 0;
}

/**
 * Consecutive tracked days ending at `todayStr`. An untracked today doesn't
 * break the streak — it just isn't counted yet (mirrors hydration).
 * Reuses the pure date math from `hydration.ts`.
 */
export function nutritionStreak(days: ReadonlyArray<NutritionDay>, todayStr: string, shift: (ymd: string, n: number) => string): number {
  const tracked = new Map<string, boolean>();
  for (const d of days) tracked.set(d.date, isDayTracked(d));

  let streak = 0;
  let cursor = tracked.get(todayStr) ? todayStr : shift(todayStr, -1);
  while (tracked.get(cursor)) {
    streak++;
    cursor = shift(cursor, -1);
  }
  return streak;
}

/** Clamp a target into a sane range. */
export function clampTargetValue(v: number, max: number): number {
  if (isNaN(v)) return 0;
  return Math.min(max, Math.max(0, Math.round(v)));
}
